import React, { useEffect, useState } from "react";
import { useEditorStore } from "../../../../../state/editorStore";
import { useActiveSubjectTransform } from "../../../../../hooks/editor/useActiveSubjectTransform";
import { RotateToolIcon } from "../../../../../icons";
import styles from "./RotatePrepCard.module.css";

/**
 * Popup-only rotate control (tool prep).
 * Header mirrors Shadow/Outline card layout, but the right side is a custom
 * angle field instead of an on/off toggle. Body is a 4×4 preset grid.
 *
 * Presets: each row ends with a cardinal (90 / 180 / 270 / 360).
 * Custom field covers any other value 0–360.
 */
export const ROTATE_PREP_PRESETS = [
  0, 30, 45, 90,
  120, 135, 150, 180,
  210, 225, 240, 270,
  300, 315, 330, 360,
];

function clampAngle(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  if (n > 360) return 360;
  if (n < 0) return 0;
  return Math.round(n);
}

const RotatePrepCard = ({ forceExpanded = true }) => {
  const { rotation, setRotation } = useActiveSubjectTransform();
  const pushSnapshot = useEditorStore((s) => s.pushSnapshot);
  const [expanded, setExpanded] = useState(Boolean(forceExpanded));
  const [draft, setDraft] = useState(String(clampAngle(rotation)));
  const isExpanded = forceExpanded || expanded;

  useEffect(() => {
    setDraft(String(clampAngle(rotation)));
  }, [rotation]);

  const commitAngle = (raw, { recordHistory = true } = {}) => {
    const next = clampAngle(raw);
    setDraft(String(next));
    setRotation(next, { recordHistory: recordHistory ? undefined : false });
  };

  const handleCustomChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    setDraft(raw);
    if (raw === "") return;
    commitAngle(raw, { recordHistory: false });
  };

  const handleCustomBlur = () => {
    commitAngle(draft === "" ? 0 : draft, { recordHistory: false });
  };

  const handlePreset = (angle) => {
    commitAngle(angle);
  };

  return (
    <div
      className={styles.card}
      onClick={() => {
        if (!forceExpanded) setExpanded((prev) => !prev);
      }}
    >
      <div className={styles.header}>
        <div className={styles.left}>
          <span className={styles.icon}>
            <RotateToolIcon />
          </span>
          <span className={styles.title}>Rotate</span>
        </div>

        <div
          className={styles.right}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className={styles.inputWrapper}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              aria-label="Custom rotate angle"
              value={draft}
              onChange={handleCustomChange}
              onFocus={() => pushSnapshot()}
              onBlur={handleCustomBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                }
              }}
              className={styles.input}
            />
            <span className={styles.degree}>°</span>
          </div>
        </div>
      </div>

      {isExpanded ? (
        <div
          className={styles.body}
          onClick={(e) => e.stopPropagation()}
        >
          <p className={styles.gridLabel}>Angle</p>
          <div className={styles.grid} role="listbox" aria-label="Rotate presets">
            {ROTATE_PREP_PRESETS.map((angle) => {
              const active = clampAngle(rotation) === angle;
              return (
                <button
                  key={angle}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`${styles.cell}${active ? ` ${styles.cellActive}` : ""}`}
                  onClick={() => handlePreset(angle)}
                >
                  {angle}°
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RotatePrepCard;
